import { Component } from '@theme/component';
import { ThemeEvents, FavoriteAddedEvent, FavoriteRemovedEvent } from '@theme/events';
import { Favorites } from '@theme/favorites';

const AUTO_DISMISS_MS = 4000;

/**
 * A toast that confirms a product was added to or removed from favorites, with an undo
 * action when removed.
 *
 * @typedef {object} Refs
 * @property {HTMLImageElement} image - The product thumbnail.
 * @property {HTMLElement} title - The bold product title.
 * @property {HTMLElement} message - The text following the product title.
 * @property {HTMLAnchorElement} viewLink - Link to the favorites page, shown when a product was added.
 * @property {HTMLButtonElement} undoButton - Re-favorites the product, shown when one was removed.
 *
 * @extends {Component<Refs>}
 */
class FavoritesToast extends Component {
  requiredRefs = ['image', 'title', 'message', 'viewLink', 'undoButton'];

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #dismissTimeout;

  /** @type {string | undefined} The handle to restore if the visitor clicks "Ongedaan maken". */
  #removedHandle;

  /** @type {number | undefined} The index to restore it at, so it lands back in its original spot. */
  #removedIndex;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.favoriteAdded, this.#onFavoriteAdded);
    document.addEventListener(ThemeEvents.favoriteRemoved, this.#onFavoriteRemoved);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.favoriteAdded, this.#onFavoriteAdded);
    document.removeEventListener(ThemeEvents.favoriteRemoved, this.#onFavoriteRemoved);
    clearTimeout(this.#dismissTimeout);
  }

  /**
   * Shows the toast for the favorited product, restarting the auto-dismiss timer.
   * @param {Event} event - The favorite-added event.
   */
  #onFavoriteAdded = (event) => {
    if (!(event instanceof FavoriteAddedEvent)) return;

    this.#removedHandle = undefined;
    this.#removedIndex = undefined;
    this.refs.message.textContent = this.dataset.messageAdded ?? '';
    this.refs.viewLink.hidden = false;
    this.refs.undoButton.hidden = true;

    this.#show(event.detail);
  };

  /**
   * Shows the toast for the unfavorited product, with an undo action, restarting the auto-dismiss timer.
   * @param {Event} event - The favorite-removed event.
   */
  #onFavoriteRemoved = (event) => {
    if (!(event instanceof FavoriteRemovedEvent)) return;

    this.#removedHandle = event.detail.handle;
    this.#removedIndex = event.detail.index;
    this.refs.message.textContent = this.dataset.messageRemoved ?? '';
    this.refs.viewLink.hidden = true;
    this.refs.undoButton.hidden = false;

    this.#show(event.detail);
  };

  /**
   * @param {{title: string, image: string}} detail - The product details to display.
   */
  #show(detail) {
    // An empty src would otherwise have the browser re-request the current page.
    this.refs.image.src = detail.image || 'data:,';
    this.refs.image.alt = detail.title;
    this.refs.title.textContent = detail.title;

    clearTimeout(this.#dismissTimeout);
    this.classList.remove('is-closing');
    this.classList.add('is-open');
    this.#dismissTimeout = setTimeout(this.dismiss, AUTO_DISMISS_MS);
  }

  /** Re-favorites the just-removed product back into its original spot, and dismisses the toast. */
  undo = () => {
    if (this.#removedHandle) Favorites.toggle(this.#removedHandle, this.#removedIndex);
    this.dismiss();
  };

  /** Hides the toast, playing the slide-down animation. */
  dismiss = () => {
    clearTimeout(this.#dismissTimeout);
    this.classList.add('is-closing');
    this.classList.remove('is-open');
  };
}

if (!customElements.get('favorites-toast')) {
  customElements.define('favorites-toast', FavoritesToast);
}
